import { useState, type FormEvent } from 'react';
import { MessageSquareText } from 'lucide-react';
import { useMaaltallAgent } from '@/hooks/use-maaltall-agent';
import { TOOL_WIDGET } from '@/lib/maaltall-agent/tools';
import { DrillPanel, DrillText } from './shared';
import { AgentAnswer } from './AgentAnswer';

const SUGGESTIONS = [
  'Hvorfor er faktureringsgraden lav i år?',
  'Hvem bør jeg ta en prat med om timeføring?',
  'Hva skjer med faktureringen etter nyttår?',
  'Hva bør jeg gjøre denne uken?',
  'Hvilke prosjekter har størst budsjettavvik?',
];

function AgentChat({ onSource }: { onSource: (widgetKey: string) => void }) {
  const { turns, ask, isPending } = useMaaltallAgent();
  const [draft, setDraft] = useState('');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    ask(draft);
    setDraft('');
  };

  return (
    <>
      <DrillText>
        Still spørsmål om tallene på Måltall. Svarene hentes fra de samme beregningene som widgetene, og hvert svar viser
        hvilke tall det bygger på.
      </DrillText>
      <div className="mt-4 flex flex-col gap-3" aria-live="polite">
        {turns.map((t, i) => (
          <div key={i} className="flex flex-col gap-3">
            <div className="max-w-[85%] self-end rounded-[14px_14px_4px_14px] bg-primary px-3.5 py-2.5 text-[14px] text-primary-foreground">
              {t.question}
            </div>
            <div className="max-w-[95%] self-start rounded-[14px_14px_14px_4px] bg-muted px-3.5 py-3 text-[14px] leading-relaxed">
              {t.error ? (
                <p className="text-destructive">{t.error}</p>
              ) : t.answer == null ? (
                <p className="text-muted-foreground">Henter tallene …</p>
              ) : (
                <AgentAnswer text={t.answer} />
              )}
              {t.sources.length > 0 && (
                <div className="mt-2.5 flex flex-wrap items-center gap-1.5 text-[12px] text-muted-foreground">
                  <span>Kilder:</span>
                  {t.sources.map((s) => (
                    <button key={s.tool} type="button" onClick={() => onSource(TOOL_WIDGET[s.tool])}
                      className="rounded-full border border-primary/20 bg-background px-2 py-0.5 font-semibold text-primary hover:bg-primary/5">
                      {s.label} ›
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {turns.length === 0 && (
        <>
          <p className="mt-5 text-[11px] font-medium uppercase tracking-[0.06em] text-[#8A97A3]">Forslag</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {SUGGESTIONS.map((s) => (
              <button key={s} type="button" onClick={() => ask(s)} disabled={isPending}
                className="rounded-full border border-foreground/15 px-2.5 py-1 text-[12.5px] text-foreground/70 hover:bg-foreground/[0.03]">
                {s}
              </button>
            ))}
          </div>
        </>
      )}
      <form onSubmit={submit} className="mt-4 flex items-center gap-2 rounded-xl border-[1.5px] border-foreground py-1.5 pl-3.5 pr-1.5">
        <label htmlFor="maaltall-agent-input" className="sr-only">Spørsmål om tallene</label>
        <input id="maaltall-agent-input" value={draft} onChange={(e) => setDraft(e.target.value)} disabled={isPending}
          placeholder="Spør om omsetning, GEEK, timer eller prosjekter …"
          className="min-w-0 flex-1 bg-transparent text-[14px] outline-none placeholder:text-muted-foreground" />
        <button type="submit" disabled={isPending || !draft.trim()}
          className="rounded-lg border border-primary/25 px-3 py-1.5 text-[13px] font-semibold text-primary disabled:opacity-50">
          Send
        </button>
      </form>
      <p className="mt-2 text-[12px] text-muted-foreground">
        Spørsmål og svar logges. Agenten kan bare lese, ikke endre noe. Navn på ansatte erstattes før spørsmålet sendes.
      </p>
    </>
  );
}

/** «Spør om tallene» for ledelsen (VIB-2992), bak feature_maaltall_agent. */
export default function MaaltallAgentPanel() {
  const [open, setOpen] = useState(false);
  const goTo = (widgetKey: string) => {
    setOpen(false);
    document.getElementById(`widget-${widgetKey}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-[14px] font-semibold text-primary-foreground hover:brightness-95">
        <MessageSquareText className="h-4 w-4" aria-hidden />
        Spør om tallene
      </button>
      <DrillPanel isOpen={open} onClose={() => setOpen(false)} kicker="Bare for ledelsen" title="Spør om tallene">
        <div className="px-4 sm:px-6 py-5">
          {open && <AgentChat onSource={goTo} />}
        </div>
      </DrillPanel>
    </>
  );
}
